import { z } from 'zod';
import { eventTypeEnum } from '../enums';
import { countryCodeSchema, isoDateTimeSchema, urlSchema } from './common';

// Admin events are authored as 'draft' or 'published' (docs/08: "Publish
// immediately or save draft"). 'cancelled' is only reachable via the cancel
// action, never the create/update payload.
export const eventAuthorStatusEnum = z.enum(['draft', 'published']);
export type EventAuthorStatus = z.infer<typeof eventAuthorStatusEnum>;

const eventBase = {
  title: z.string().trim().min(2).max(200),
  description: z.string().trim().max(5000).nullable().optional(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema.nullable().optional(),
  allDay: z.boolean().default(false),
  timezone: z.string().max(64).default('Europe/London'),
  location: z.string().trim().max(300).nullable().optional(),
  url: urlSchema.nullable().optional(),
};

// Admin events: platform scope (system admin) or concept scope (concept admin).
// Scope + concept are derived from the route/caller, never the payload.
export const createAdminEventSchema = z.object({
  ...eventBase,
  eventType: eventTypeEnum.exclude(['garage']),
  countryCodes: z.array(countryCodeSchema).optional(),
  // Save as draft or publish immediately (docs/08). Defaults to draft so an
  // admin event is staged, not instantly live in members' calendars.
  status: eventAuthorStatusEnum.default('draft'),
});
export type CreateAdminEventPayload = z.infer<typeof createAdminEventSchema>;

export const updateAdminEventSchema = createAdminEventSchema.partial();
export type UpdateAdminEventPayload = z.infer<typeof updateAdminEventSchema>;

// Lifecycle action on an admin event (docs/08): stage→live or live→staged.
// Cancellation has its own endpoint and is not part of this action set.
export const eventStatusActionSchema = z.object({ action: z.enum(['publish', 'unpublish']) });
export type EventStatusAction = z.infer<typeof eventStatusActionSchema>;

// Garage events (docs/08): simple fields, always type 'garage', garage-wide.
export const createGarageEventSchema = z.object(eventBase);
export type CreateGarageEventPayload = z.infer<typeof createGarageEventSchema>;

export const updateGarageEventSchema = createGarageEventSchema.partial();
export type UpdateGarageEventPayload = z.infer<typeof updateGarageEventSchema>;

export const eventListQuerySchema = z.object({
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  types: z.string().optional(), // comma-separated event types (filter chips)
});
export type EventListQuery = z.infer<typeof eventListQuerySchema>;
